import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const TermsConditions = () => {
  const navigate = useNavigate();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Terms and Conditions | Aspirant";
  }, []);

  return (
    <div className="bg-[#06010a] text-white font-poppins overflow-x-hidden">
      {/* Hero Section */}
      <section className="flex flex-col justify-center items-center text-center min-h-[60vh] px-4 bg-gradient-to-b from-[#06010a] to-[#260345]">
        <h1 className="text-4xl md:text-6xl font-bold mb-6">
          Terms &{" "}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-purple-600 font-extrabold">
            Conditions
          </span>
        </h1>

        <p className="text-lg text-gray-300 max-w-2xl">
          Please read these terms carefully before using Novistep and Aspirant Suite. 
          By using our platform, you agree to these legally binding terms.
        </p>

        <button
          onClick={() => navigate("/")}
          className="mt-8 bg-[#ffc403] text-black font-bold px-6 py-3 rounded-xl hover:bg-[#e6b800] transition"
        >
          Back to Home
        </button>
      </section>

      {/* Content Section */}
      <section className="bg-white text-black py-20 px-6 md:px-8">
        <div className="max-w-4xl mx-auto space-y-10">
          
          {/* Intro */}
          <div>
            <h2 className="text-2xl font-bold mb-3">1. Platform Purpose & Scope</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Novistep (also referred to as “Aspirant”, “we”, or “us”) is an AI-powered career development and study-abroad assistance platform. Our services include:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Resume creation and AI-based optimization</li>
              <li>AI interview simulations and skill gap analysis</li>
              <li>Job matching and career recommendations</li>
              <li>Study abroad planning and university application guidance</li>
              <li>Educational resources, blogs, and tools</li>
            </ul>
            <p className="text-gray-500 text-sm mt-4 italic">
              Note: The Platform provides guidance only and does not replace professional advisors or legal authorities.
            </p>
          </div>

          {/* Eligibility */}
          <div>
            <h2 className="text-2xl font-bold mb-3">2. Eligibility</h2>
            <p className="text-gray-700 leading-relaxed">
              To use the Platform, you must be at least 18 years old, or have parental/legal guardian consent. By using the Platform, you confirm that the information you provide is accurate and that you are legally capable of entering into this agreement.
            </p>
          </div>

          {/* Account Security */}
          <div>
            <h2 className="text-2xl font-bold mb-3">3. Account Registration & Security</h2>
            <p className="text-gray-700 leading-relaxed">
              You are responsible for maintaining the confidentiality of your account credentials. You agree to provide accurate information and update it as needed. Novistep is not liable for unauthorized access caused by your failure to safeguard your account.
            </p>
          </div>

          {/* AI Disclaimer */}
          <div>
            <h2 className="text-2xl font-bold mb-3">4. AI-Generated Content Disclaimer</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our Platform uses AI to generate resume suggestions, interview questions, and career insights.
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>All outputs are informational and advisory only.</li>
              <li>AI outputs do not guarantee job placement, interview success, or visa approval.</li>
              <li>Users must independently verify all outputs before official use.</li>
            </ul>
          </div>

          {/* Study Abroad */}
          <div>
            <h2 className="text-2xl font-bold mb-3">5. Study Abroad & Admissions</h2>
            <p className="text-gray-700 leading-relaxed">
              While we provide guidance on universities and visas, we are not an immigration authority. Final admission and visa decisions rest solely with the respective educational institutions and government bodies.
            </p>
          </div>

          {/* User Content */}
          <div>
            <h2 className="text-2xl font-bold mb-3">6. User Content & Responsibilities</h2>
            <p className="text-gray-700 leading-relaxed">
              You confirm that any content you upload (resumes, documents) is truthful and does not violate third-party rights. Prohibited content includes fraudulent, plagiarized, or offensive material.
            </p>
          </div>

          {/* Payments */}
          <div>
            <h2 className="text-2xl font-bold mb-3">7. Payments & Subscriptions</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Prices for paid plans will be clearly disclosed at the time of purchase.</li>
              <li>Payments are generally non-refundable unless stated otherwise.</li>
              <li>We reserve the right to modify pricing with prior notice.</li>
            </ul>
          </div>

          {/* Liability */}
          <div>
            <h2 className="text-2xl font-bold mb-3">8. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              To the maximum extent permitted by law, Novistep is not liable for indirect, incidental, or consequential damages. Use of the Platform and reliance on AI-generated outputs is at your own risk.
            </p>
          </div>

          {/* Governing Law */}
          <div>
            <h2 className="text-2xl font-bold mb-3">9. Governing Law</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts located in India.
            </p>
          </div>

          {/* Contact Box */}
          <div className="bg-gray-50 border rounded-xl p-6">
            <h2 className="text-xl font-semibold mb-2">Contact Legal Support</h2>
            <p className="text-gray-700 text-sm mb-4">
              If you have any questions regarding these Terms and Conditions, please reach out to our team.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="mailto:info@novistep.com"
                className="inline-block bg-yellow-400 text-black font-semibold px-6 py-2 rounded-lg hover:bg-yellow-500 transition"
              >
                Email Support
              </a>
              <a
                href="https://job.novistep.com"
                target="_blank"
                rel="noreferrer"
                className="inline-block bg-gray-200 text-black font-semibold px-6 py-2 rounded-lg hover:bg-gray-300 transition"
              >
                Visit Website
              </a>
            </div>
          </div>
          
          <p className="text-center text-gray-400 text-sm pt-10">
            Last Updated: October 24, 2023 | © {new Date().getFullYear()} Novistep / Aspirant.
          </p>
        </div>  
      </section>
    </div>
  );
};

export default TermsConditions;